const BASE_URL = 'https://api.vectorvault.io/';

export const conversationAPI = {
  // Get customers with activity stats
  async getCustomers(vectorVault, params = {}) {
    const {
      page = 0,
      page_size = 25,
      search = '',
      sort_by = 'last_activity_at',
      sort_order = 'DESC'
    } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      page_size: page_size.toString(),
      sort_by,
      sort_order,
      ...(search && { search })
    });

    try {
      const response = await vectorVault.makeAuthenticatedRequest(`${BASE_URL}sql/customers?${queryParams}`, {
        method: 'GET'
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch customers: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching customers:', error);
      throw error;
    }
  },

  // Get sessions/conversations with filtering (NOT for content search - use searchSessions instead)
  async getSessions(vectorVault, params = {}) {
    const {
      customer_id,
      page = 0,
      page_size = 25,
      flow_name,
      search = '', // This searches session titles, not message content
      sort_by = 'last_message_at',
      sort_order = 'DESC'
    } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      page_size: page_size.toString(),
      sort_by,
      sort_order,
      ...(customer_id && { customer_id }),
      ...(flow_name && { flow_name }),
      ...(search && { search })
    });

    try {
      const response = await vectorVault.makeAuthenticatedRequest(`${BASE_URL}sql/sessions?${queryParams}`, {
        method: 'GET'
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch sessions: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching sessions:', error);
      throw error;
    }
  },

  // Get messages from a specific session
  async getSessionMessages(vectorVault, sessionId, params = {}) {
    const {
      page = 0,
      page_size = 25,
      message_type,
      search = '', // This searches within the single session only
      sort_by = 'timestamp',
      sort_order = 'ASC'
    } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      page_size: page_size.toString(),
      sort_by,
      sort_order,
      ...(message_type && { message_type }),
      ...(search && { search })
    });

    try {
      const response = await vectorVault.makeAuthenticatedRequest(`${BASE_URL}sql/sessions/${sessionId}/messages?${queryParams}`, {
        method: 'GET'
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch session messages: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching session messages:', error);
      throw error;
    }
  },

  // DEBUG: Check database state and test basic search
  async debugSearch(vectorVault, searchQuery = '') {
    const queryParams = new URLSearchParams();
    if (searchQuery.trim()) {
      queryParams.set('q', searchQuery.trim());
    }

    try {
      const response = await vectorVault.makeAuthenticatedRequest(`${BASE_URL}sql/debug/search?${queryParams}`, {
        method: 'GET'
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to debug search: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error in debug search:', error);
      throw error;
    }
  },

  // Search across all messages in conversations (database-backed)
  async searchMessages(vectorVault, searchQuery, params = {}) {
    const {
      page = 0,
      page_size = 25,
      message_type, // 'user', 'assistant', 'system'
      flow_name     // Optional flow filter
    } = params;

    if (!searchQuery.trim()) {
      throw new Error('Search query is required');
    }

    const queryParams = new URLSearchParams({
      q: searchQuery.trim(),
      page: page.toString(),
      page_size: page_size.toString(),
      ...(message_type && { message_type }),
      ...(flow_name && { flow_name })
    });

    try {
      const response = await vectorVault.makeAuthenticatedRequest(`${BASE_URL}sql/search/messages?${queryParams}`, {
        method: 'GET'
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to search messages: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error searching messages:', error);
      throw error;
    }
  },

  // Search sessions by their message content (database-backed)
  async searchSessions(vectorVault, searchQuery, params = {}) {
    const {
      page = 0,
      page_size = 25,
      flow_name     // Optional flow filter
    } = params;

    if (!searchQuery.trim()) {
      throw new Error('Search query is required');
    }

    const queryParams = new URLSearchParams({
      q: searchQuery.trim(),
      page: page.toString(),
      page_size: page_size.toString(),
      ...(flow_name && { flow_name })
    });

    try {
      const response = await vectorVault.makeAuthenticatedRequest(`${BASE_URL}sql/search/sessions?${queryParams}`, {
        method: 'GET'
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to search sessions: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error searching sessions:', error);
      throw error;
    }
  },

  // Unified search across both messages and sessions (database-backed)
  async unifiedSearch(vectorVault, searchQuery, params = {}) {
    const {
      page = 0,
      page_size = 25,
      type = 'all',  // 'messages', 'sessions', or 'all'
      flow_name      // Optional flow filter
    } = params;

    if (!searchQuery.trim()) {
      throw new Error('Search query is required');
    }

    const queryParams = new URLSearchParams({
      q: searchQuery.trim(),
      page: page.toString(),
      page_size: page_size.toString(),
      type,
      ...(flow_name && { flow_name })
    });

    try {
      const response = await vectorVault.makeAuthenticatedRequest(`${BASE_URL}sql/search?${queryParams}`, {
        method: 'GET'
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to perform unified search: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error in unified search:', error);
      throw error;
    }
  },

  // Get overall statistics
  async getStats(vectorVault) {
    try {
      const response = await vectorVault.makeAuthenticatedRequest(`${BASE_URL}sql/stats`, {
        method: 'GET'
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch stats: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching stats:', error);
      throw error;
    }
  },

  // Delete a session
  async deleteSession(vectorVault, sessionId, customerId) {
    try {
      const response = await vectorVault.makeAuthenticatedRequest(`${BASE_URL}sql/sessions/${sessionId}`, {
        method: 'DELETE',
        body: JSON.stringify({ customer_id: customerId })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete session: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting session:', error);
      throw error;
    }
  }
};